// The Municipal Bulletin — data access.
//
// Bulletins are authored in the admin portal and served by web-api
// (`GET /bulletin`). This module is the single seam between that API and the
// public bulletin UI: it maps the API payload onto the `BulletinIssue` shape the
// routes render and exposes react-query hooks. Pure display helpers
// (category labels, tag/date formatting) live here too so routes import one place.

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  BulletinArticle,
  BulletinCategory,
  BulletinCategoryOption,
  BulletinSummary,
} from '@munifocus/contracts-web';
import { api } from '../lib/api-client';
import { frenchFirstOfMonth } from '@munifocus/utils';

export type { BulletinCategory } from '@munifocus/contracts-web';

export interface BulletinIssue {
  slug: string;
  /** The locale this payload is actually rendered in (may fall back to source). */
  language: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  title: string;
  author: string;
  keywords: string[];
  category: BulletinCategory;
  /** Plain-text editorial excerpt for list/preview cards. */
  excerpt: string;
}

export interface BulletinDetail extends BulletinIssue {
  /** Article body Markdown. */
  body: string;
}

/**
 * Labels for the categories this build shipped with.
 *
 * Categories are editable data in the admin, so this is a *fallback* only — it
 * covers the first paint before `useBulletinCategories` resolves, and any
 * offline/failed fetch. Anything not listed here resolves through the API, and
 * failing that falls back to the slug itself. Never index this map directly:
 * a category created after this build would be `undefined` and crash the page.
 * Use {@link useCategoryLabel}.
 */
const FALLBACK_CATEGORY_LABELS: Record<string, { en: string; fr: string }> = {
  governance: { en: 'Governance & Ethics', fr: 'Gouvernance et déontologie' },
  enforcement: { en: 'Enforcement', fr: 'Application des règlements' },
  housing: { en: 'Housing & Planning', fr: 'Logement et urbanisme' },
  finance: { en: 'Finance & Oversight', fr: 'Finances et surveillance' },
};

/** The category catalogue as published by web-api. */
export function useBulletinCategories() {
  const { data } = useQuery({
    queryKey: ['bulletin-categories'],
    queryFn: async () => (await api.get<BulletinCategoryOption[]>('/bulletin/categories')).data,
    staleTime: 5 * 60 * 1000,
  });
  return Array.isArray(data) ? data : [];
}

/**
 * Resolve a category slug to a label in the current language: the live
 * catalogue first, then this build's fallback map, then the slug itself. Always
 * returns a string, so an unknown category degrades to a readable chip rather
 * than a crash.
 */
export function useCategoryLabel(): (slug: string) => string {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  const categories = useBulletinCategories();
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  return (slug: string) => {
    const live = bySlug.get(slug);
    if (live) return lang === 'fr' ? live.labelFr : live.labelEn;
    return FALLBACK_CATEGORY_LABELS[slug]?.[lang] ?? slug;
  };
}

function currentLang(language: string | undefined): 'en' | 'fr' {
  return language?.startsWith('fr') ? 'fr' : 'en';
}

function summaryToIssue(s: BulletinSummary): BulletinIssue {
  return {
    slug: s.slug,
    language: s.language,
    date: s.publishedAt.slice(0, 10),
    title: s.title,
    author: s.author,
    keywords: s.keywords,
    category: s.category,
    excerpt: s.excerpt,
  };
}

/**
 * Fetch the published bulletins, optionally narrowed by a reader's search term.
 * The search runs server-side (web-api matches title, body, keywords, author, and
 * category) so content — not just headlines — is searchable. Pass the *debounced*
 * query so a new request fires only when typing pauses. Previous results are kept
 * on screen while the next set loads, so the list never flashes empty mid-search.
 */
export function useBulletins(search?: string) {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  const q = search?.trim() ?? '';
  return useQuery({
    queryKey: ['bulletins', lang, q],
    queryFn: async () => {
      const { data } = await api.get<BulletinSummary[]>('/bulletin', {
        params: { lang, ...(q ? { search: q } : {}) },
      });
      return data.map(summaryToIssue);
    },
    placeholderData: keepPreviousData,
  });
}

export function useBulletin(slug: string | undefined) {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  return useQuery({
    queryKey: ['bulletin', slug, lang],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.get<BulletinArticle>(`/bulletin/${slug}`, { params: { lang } });
      return { ...summaryToIssue(data), body: data.body } satisfies BulletinDetail;
    },
  });
}

// Article keywords are authored lowercase; display them in sentence case to match
// the design system. Capitalising only the first character preserves embedded
// acronyms and proper nouns (NB, UBCM, Bill 49, Local Governance Act).
export function formatTag(label: string): string {
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
}

export function formatBulletinDate(iso: string, lang: 'en' | 'fr'): string {
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return frenchFirstOfMonth(
    date.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  );
}
